import React from "react"

import { StepIndicator } from "@/components/flow-progress-bar/StepIndicator"
import { SubstepIndicator } from "@/components/flow-progress-bar/SubstepIndicator"
import { useFlowProgress } from "@/components/flow-progress-bar/useFlowProgress"
import type { FlowStateMachine, FlowStep } from "@/lib/flows/base/types"
import { cn } from "@/lib/utils"

import type { createActorContext } from "@xstate/react"
import type { AnyMachineSnapshot } from "xstate"

type FlowProgressBarProps<
  TMachine extends FlowStateMachine = FlowStateMachine,
> = {
  FlowContext: ReturnType<typeof createActorContext<TMachine>>
  flowSteps: FlowStep[]
}

export function FlowProgressBar<
  TMachine extends FlowStateMachine = FlowStateMachine,
>({ FlowContext, flowSteps }: FlowProgressBarProps<TMachine>) {
  const currentStateValue = FlowContext.useSelector(
    (snapshot: AnyMachineSnapshot) => {
      return snapshot.value
    },
  )

  const progress = useFlowProgress(currentStateValue, flowSteps)

  if (progress.steps.length === 0) {
    return null
  }

  const activeStepIndex = progress.steps.findIndex(
    (step) => step.status === "active",
  )
  const currentStepIndex =
    activeStepIndex >= 0 ? activeStepIndex : progress.steps.length - 1
  const currentStep = progress.steps[currentStepIndex]

  return (
    <div className="w-full min-w-0 rounded-3xl bg-white">
      <div className="space-y-2 px-4 py-4 lg:hidden">
        <p className="text-muted-foreground text-xs font-medium">
          Step {currentStepIndex + 1} of {progress.steps.length}
        </p>
        <p className="text-base leading-tight font-semibold">
          {currentStep.title}
        </p>
        {currentStep.totalSubsteps > 1 && (
          <SubstepIndicator
            currentSubstep={currentStep.currentSubstep || 1}
            totalSubsteps={currentStep.totalSubsteps}
          />
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <div className="flex min-w-max items-center px-6 py-5">
          {progress.steps.map((step, idx) => {
            const isLastStep = idx === progress.steps.length - 1

            return (
              <React.Fragment key={step.id}>
                <StepIndicator
                  step={step.title}
                  status={step.status}
                  substep={step.currentSubstep}
                  totalSubsteps={step.totalSubsteps}
                />
                {!isLastStep && (
                  <ProgressLine isCompleted={step.status === "completed"} />
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const ProgressLine = ({ isCompleted }: { isCompleted: boolean }) => (
  <div className="mx-2 h-0.5 w-10 shrink-0 lg:mx-3 lg:w-14 xl:w-20">
    <div
      className={cn(
        "h-full w-full rounded-full transition-colors",
        isCompleted ? "bg-primary" : "bg-muted/40",
      )}
    />
  </div>
)
